import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getColorProduct } from '../../actions';
import OrderDialog from './OrderDialog';
import {
  FENCE_LAYOUT_LABELS, calcFence, categoryUnit, formatCoef, formatMeters, formatPieces, idOf,
  itemBaseHeight, itemBasePricePerMeter, itemFenceLayout, itemMeters, itemName, itemPrice,
  itemPricePerMeter, itemUnit, money, resolveFenceLayout, roundMoney,
} from './orderView';

// Поля, от которых считается ограда: меняется любое — метры и цена пересчитываются.
const FENCE_KEYS = ['length', 'width', 'height', 'fenceLayout', 'basePricePerMeter', 'baseHeightMm'];

const fenceOf = (state) => calcFence({ ...state, layout: state.fenceLayout });

export default function OrderItemEditor({ item, onClose, mutate, busy }) {
  const dispatch = useDispatch();
  const colors = useSelector((state) => state.colorProduct?.colorProducts || []);
  // Способ расчёта: сохранённый (или по данным калькулятора), а если его нет — как считает категория.
  const current = itemUnit(item);
  const suggested = categoryUnit(item);
  const catalogPerMeter = Number(item.productId?.pricePerMeter) > 0 ? roundMoney(item.productId.pricePerMeter) : null;
  const [form, setForm] = useState(() => {
    const initial = {
      priceUnit: current === 'linearMeter' ? 'linearMeter' : suggested,
      bayerPrice: item.priceConfirmed === false ? '' : itemPrice(item) ?? '',
      purchasedQty: item.purchasedQty ?? 1,
      linearMeters: itemMeters(item) ?? '',
      pricePerMeter: itemPricePerMeter(item) ?? catalogPerMeter ?? '',
      fenceLayout: itemFenceLayout(item),
      basePricePerMeter: itemBasePricePerMeter(item) ?? '',
      baseHeightMm: itemBaseHeight(item) ?? '',
      length: item.length ?? '', width: item.width ?? '', height: item.height ?? '',
      typeProductColor: idOf(item.typeProductColor),
    };
    // Метры и цена за метр сразу показываются по размерам (как на сайте).
    // Цену штуки при открытии не трогаем: если она другая — ниже будет кнопка «Пересчитать».
    if (initial.priceUnit === 'linearMeter') {
      const fence = fenceOf(initial);
      if (fence.meters != null) initial.linearMeters = String(fence.meters);
      if (fence.pricePerMeter != null) initial.pricePerMeter = String(fence.pricePerMeter);
    }
    return initial;
  });
  /** Метры и цену за метр ввели вручную — по размерам их не пересчитываем. */
  const [manual, setManual] = useState(false);
  useEffect(() => { dispatch(getColorProduct()); }, [dispatch]);

  const linear = form.priceUnit === 'linearMeter';
  const fence = fenceOf(form);
  // Метры зафиксированы, если их можно посчитать по размерам; цена за метр — если есть цена по каталогу.
  const metersLocked = linear && !manual && fence.meters != null;
  const perMeterLocked = linear && !manual && fence.pricePerMeter != null;
  const meters = Number(form.linearMeters);
  const perMeter = Number(form.pricePerMeter);
  /** Цена одной штуки по метрам: метры × цена за метр. */
  const byMeters = form.linearMeters !== '' && form.pricePerMeter !== '' && meters > 0 && perMeter >= 0 ? roundMoney(meters * perMeter) : null;
  const price = form.bayerPrice === '' ? null : Number(form.bayerPrice);
  const qty = Number(form.purchasedQty);
  const sum = price != null && Number.isFinite(price) && Number.isFinite(qty) ? roundMoney(price * qty) : null;
  // Цена штуки отличается от «метры × цена за метр» — например, в неё входит оформление.
  const priceDiffers = linear && byMeters != null && price != null && Math.abs(byMeters - price) > 0.5;
  // Категория считается по метрам, а данных о метрах у позиции ещё нет.
  const needsMeters = suggested === 'linearMeter' && current !== 'linearMeter';
  const autoLayout = resolveFenceLayout('', form.length, form.width);

  /** Пересчёт ограды по формуле сайта: метры, цена за метр и цена одной штуки. */
  const applyFence = (state) => {
    if (state.priceUnit !== 'linearMeter') return state;
    const result = fenceOf(state);
    const next = { ...state };
    if (result.meters != null) next.linearMeters = String(result.meters);
    if (result.pricePerMeter != null) next.pricePerMeter = String(result.pricePerMeter);
    const m = Number(next.linearMeters);
    const p = Number(next.pricePerMeter);
    if (next.linearMeters !== '' && next.pricePerMeter !== '' && m > 0 && p >= 0) {
      next.bayerPrice = String(result.total != null ? result.total : roundMoney(m * p));
    }
    return next;
  };
  const set = (key, value) => setForm((state) => {
    const next = { ...state, [key]: value };
    if (manual) return next;
    // Включили «по погонным метрам» или изменили размеры ограды — считаем по формуле.
    if ((key === 'priceUnit' && value === 'linearMeter') || (FENCE_KEYS.includes(key) && next.priceUnit === 'linearMeter')) return applyFence(next);
    return next;
  });
  /** Метры или цену за метр ввели вручную — пересчитываем только цену штуки. */
  const setLinear = (key, value) => setForm((state) => {
    const next = { ...state, [key]: value };
    const m = Number(next.linearMeters);
    const p = Number(next.pricePerMeter);
    if (next.linearMeters !== '' && next.pricePerMeter !== '' && m > 0 && p >= 0) next.bayerPrice = String(roundMoney(m * p));
    return next;
  });
  /** «Ввести вручную» / «Считать по размерам». */
  const toggleManual = () => {
    if (manual) setForm((state) => applyFence(state));
    setManual((value) => !value);
  };
  // step="any": иначе браузер не даёт сохранить, например, 12 метров при min="0.001".
  const field = (key, label, options = {}, onChange = set) => <label className="orders-field"><span>{label}</span><input type="number" value={form[key]} min="0" step="any" {...options} onChange={(event) => onChange(key, event.target.value)} /></label>;

  // Поштучно — сведения об ограде убираем, чтобы позиция не показывалась как «погонная».
  const submit = () => mutate('item', {
    ...form,
    ...(linear ? {} : { linearMeters: '', pricePerMeter: '', fenceLayout: '', basePricePerMeter: '', baseHeightMm: '' }),
    productOrderDetailsId: item._id,
    typeSet: 'item',
  });

  return <OrderDialog title={`Позиция: ${itemName(item)}`} onClose={onClose} busy={busy} onSubmit={submit}>
    <p className="orders-caption">{linear ? 'Ограда считается как на сайте: погонные метры × цена за метр с учётом высоты. Количество — сколько штук (оград).' : 'Цена указывается за единицу в рублях.'} После сохранения итог пересчитается и потребуется подтвердить стоимость заказа.</p>
    {needsMeters && <p className="orders-caption">Категория товара считается по погонным метрам. Укажите размеры и цену за метр — цена посчитается сама.</p>}
    <div className="orders-form-grid">
      <label className="orders-field"><span>Как считать цену</span><select value={form.priceUnit} onChange={(event) => set('priceUnit', event.target.value)}><option value="piece">Поштучно</option><option value="linearMeter">По погонным метрам</option></select></label>
      {linear && <label className="orders-field"><span>Форма ограды</span><select value={form.fenceLayout} onChange={(event) => set('fenceLayout', event.target.value)}><option value="">По размерам ({FENCE_LAYOUT_LABELS[autoLayout].toLowerCase()})</option><option value="perimeter">Ограда участка: 2 × (Д + Ш)</option><option value="straight">Прямой забор: по длине</option></select></label>}
      {field('length', 'Длина, мм')}{field('width', 'Ширина, мм')}{field('height', 'Высота, мм')}
      {linear && field('basePricePerMeter', 'Цена за пог. м по каталогу, ₽')}
      {linear && field('baseHeightMm', 'Высота 3D-модели (база), мм', { min: '1' })}
      {linear && field('linearMeters', metersLocked ? 'Погонные метры на 1 шт. (по размерам)' : 'Погонные метры на 1 шт., м', { required: true, min: '0.001', readOnly: metersLocked, title: metersLocked ? 'Считается по длине и ширине. Чтобы ввести своё — «Ввести вручную»' : undefined }, setLinear)}
      {linear && field('pricePerMeter', perMeterLocked ? 'Цена за пог. м с учётом высоты (по формуле)' : 'Цена за пог. м с учётом высоты, ₽', { required: true, readOnly: perMeterLocked, title: perMeterLocked ? 'Считается по цене каталога и высоте. Чтобы ввести своё — «Ввести вручную»' : undefined }, setLinear)}
      {field('bayerPrice', linear ? 'Цена за 1 шт., ₽' : 'Согласованная цена за единицу, ₽', { required: true })}
      {field('purchasedQty', 'Количество, шт.', { required: true, min: '0.000001' })}
      <label className="orders-field"><span>Цвет</span><select value={form.typeProductColor} onChange={(event) => set('typeProductColor', event.target.value)}><option value="">Не указан</option>{form.typeProductColor && !colors.some((color) => color._id === form.typeProductColor) && <option value={form.typeProductColor}>{item.typeProductColor?.name || 'Текущий цвет'}</option>}{colors.map((color) => <option key={color._id} value={color._id}>{color.name}</option>)}</select></label>
    </div>
    {linear && <>
      <p className="orders-caption">
        {fence.layout === 'straight' ? 'Прямой забор: метры = длина' : 'Ограда участка: метры = 2 × (длина + ширина), калитка входит'}
        {fence.meters != null ? ` = ${formatMeters(fence.meters)}` : ' — укажите размеры'}.
        {' '}Цена за метр: {fence.pricePerMeter != null ? `${money(form.basePricePerMeter)}${fence.coef > 1 ? ` ${formatCoef(fence.coef)} за высоту` : ''} = ${money(fence.pricePerMeter)}` : 'укажите цену по каталогу'}.
        {manual ? ' Сейчас метры и цена за метр введены вручную.' : ''}
      </p>
      {!(Number(form.baseHeightMm) > 0) && <p className="orders-caption">Высота 3D-модели не известна — цена за метр без надбавки за высоту. Укажите её, чтобы высота учитывалась.</p>}
      {fence.note && <p className="orders-caption">{fence.note}.</p>}
      <div className="orders-actions">
        {(fence.meters != null || fence.pricePerMeter != null || manual) && <button type="button" className="orders-btn" disabled={busy} onClick={toggleManual} title={manual ? 'Снова считать метры и цену за метр по размерам' : 'Ввести метры и цену за метр самому'}>{manual ? 'Считать по размерам' : 'Ввести вручную'}</button>}
        {catalogPerMeter && Number(form.basePricePerMeter) !== catalogPerMeter && <button type="button" className="orders-btn" disabled={busy} onClick={() => set('basePricePerMeter', String(catalogPerMeter))} title="Цена за погонный метр из карточки товара">Цена из карточки товара: {money(catalogPerMeter)} за м</button>}
        {priceDiffers && <button type="button" className="orders-btn" disabled={busy} onClick={() => setForm((state) => ({ ...state, bayerPrice: String(byMeters) }))} title="Поставить цену штуки = метры × цена за метр">Пересчитать цену по метрам: {money(byMeters)}</button>}
      </div>
      {priceDiffers && <p className="orders-caption">Цена штуки отличается от «метры × цена за метр» ({money(byMeters)}): возможно, в неё входят работы или оформление. Оставьте как есть или пересчитайте.</p>}
      {byMeters != null && <p className="orders-caption">За 1 шт.: {formatMeters(meters)} × {money(perMeter)} = <b>{money(byMeters)}</b></p>}
    </>}
    <p className="orders-caption">Сумма позиции: {Number.isFinite(qty) ? formatPieces(qty) : '—'} × {money(price)} = <b>{money(sum)}</b></p>
  </OrderDialog>;
}
